import { Injectable, inject } from '@angular/core';
import { PLATFORM_CONFIG } from '@platform/config';
import type { PlatformConfig } from '@platform/config';

/**
 * Service to access platform configuration
 * Provides type-safe access to configuration values
 */
@Injectable({
  providedIn: 'root',
})
export class ConfigService {
  private readonly config = inject<PlatformConfig>(PLATFORM_CONFIG);

  /**
   * Get the entire platform configuration
   */
  getConfig(): PlatformConfig {
    return this.config;
  }

  /**
   * Get meta configuration
   */
  getMeta() {
    return this.config.meta;
  }

  /**
   * Get branding configuration
   */
  getBranding() {
    return this.config.branding;
  }

  /**
   * Get theme configuration
   */
  getTheme() {
    return this.config.theme;
  }

  /**
   * Get i18n configuration
   */
  getI18n() {
    return this.config.i18n;
  }

  /**
   * Get layout configuration
   */
  getLayout() {
    return this.config.layout;
  }

  /**
   * Get modules configuration
   */
  getModules() {
    return this.config.modules;
  }

  /**
   * Get features configuration
   */
  getFeatures() {
    return this.config.features;
  }

  /**
   * Get auth configuration
   */
  getAuth() {
    return this.config.auth;
  }

  /**
   * Get integrations configuration
   */
  getIntegrations() {
    return this.config.integrations;
  }

  /**
   * Get API configuration
   */
  getApi() {
    return this.config.api;
  }

  /**
   * Check if debug mode is enabled
   */
  isDebug(): boolean {
    return this.config.debug ?? false;
  }

  /**
   * Get current environment
   */
  getEnvironment(): string {
    return this.config.environment ?? 'development';
  }

  /**
   * Get app version
   */
  getVersion(): string {
    return this.config.version ?? '1.0.0';
  }
}

